import { Program } from '@/components/drill-planner/types/program-types';

export const downloadProgram = (program: Program): void => {
    const dataStr = JSON.stringify(program, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${program.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
};
import logger from '@/lib/logging';

export const uploadProgram = (): Promise<Program | null> => {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        
        input.onchange = (e: Event) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                resolve(null);
                return;
            }
            
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const program = JSON.parse(event.target?.result as string) as Program;
                    resolve(program);
                } catch (error) {
                    logger.error('Error parsing program JSON:', error);
                    reject(new Error('Invalid program file format'));
                }
            };
            
            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };
            
            reader.readAsText(file);
        };
        
        input.click();
    });
};

// PDF Export
export const downloadProgramAsPDF = async (program: Program): Promise<void> => {
    // For now, create a simple text-based PDF representation
    // In production, you'd use a library like jsPDF or pdfkit
    const content = `Program: ${program.name}\n\n${program.description || ''}\n\nDays: ${program.days.length}\n\n`;
    const dataStr = JSON.stringify(program, null, 2);
    const fullContent = content + '\n\nProgram Data:\n' + dataStr;
    
    const dataBlob = new Blob([fullContent], { type: 'text/plain' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${program.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    
    // TODO: Implement actual PDF generation with jsPDF or similar
    throw new Error('PDF export not yet implemented. Please use JSON export for now.');
};

// ── Drill Plan export (new DrillPlan type, for the mobile wizard) ──────────────

import type { DrillPlan } from '@/components/drill-planner/types/drill-plan-types';
import type { Drill } from '@/components/drill-library/types/drill-types';

/**
 * Opens a print-ready HTML page in a new tab containing the full drill plan.
 * On iOS / Android the browser's native "Print → Save as PDF" handles the rest.
 */
export const printDrillPlan = (
  plan: Omit<DrillPlan, 'id' | 'createdAt' | 'updatedAt'> & { id?: string },
  drillById: Record<string, Drill>,
  clientName?: string,
): void => {
  const rows = plan.days
    .map((day, di) => {
      const slots = plan.slotLabels ? Object.keys(plan.slotLabels) : Object.keys(day.slots);
      const slotRows = slots
        .map((sid) => {
          const slot = day.slots[sid];
          if (!slot) return '';
          const drill = drillById[slot.drillId];
          const label = plan.slotLabels?.[sid] ?? sid;
          const sets = slot.sets || '—';
          const reps = slot.reps || '—';
          const rest = slot.rest || '—';
          return `<tr>
            <td>${label}</td>
            <td><strong>${drill?.name ?? slot.drillId}</strong>${drill?.videoUrl ? ` <a href="${drill.videoUrl}" target="_blank" style="font-size:11px;color:#7c3aed">[video]</a>` : ''}</td>
            <td style="text-align:center">${sets}</td>
            <td style="text-align:center">${reps}</td>
            <td style="text-align:center">${rest}</td>
          </tr>`;
        })
        .filter(Boolean)
        .join('');

      return `<div class="day">
        <h2>Day ${di + 1} — ${day.name}</h2>
        <table>
          <thead><tr><th>Slot</th><th>Drill</th><th>Sets</th><th>Reps</th><th>Rest</th></tr></thead>
          <tbody>${slotRows}</tbody>
        </table>
      </div>`;
    })
    .join('');

  const tags = Array.isArray(plan.tags) ? plan.tags.join(' · ') : '';
  const twiceDaily = plan.twiceDaily ? '<span class="badge">2× daily</span>' : '';
  const phase = plan.phase ? `<span class="badge">${plan.phase}</span>` : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${plan.name}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, Arial, sans-serif; padding: 24px; color: #111; font-size: 14px; }
    h1 { font-size: 22px; margin-bottom: 4px; }
    .meta { color: #555; font-size: 12px; margin-bottom: 16px; }
    .badge { display: inline-block; background: #ede9fe; color: #7c3aed; border-radius: 4px; padding: 2px 7px; font-size: 11px; font-weight: 600; margin-right: 4px; }
    .day { margin-bottom: 24px; page-break-inside: avoid; }
    h2 { font-size: 15px; margin-bottom: 8px; color: #374151; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { background: #f3f4f6; text-align: left; padding: 6px 8px; font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
    td { padding: 7px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
    tr:last-child td { border-bottom: none; }
    .footer { margin-top: 32px; font-size: 11px; color: #9ca3af; }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <button class="no-print" onclick="window.print()" style="margin-bottom:16px;padding:8px 18px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;cursor:pointer;">
    Save as PDF / Print
  </button>
  <h1>${plan.name}</h1>
  <p class="meta">
    ${clientName ? `Client: <strong>${clientName}</strong> &nbsp;·&nbsp; ` : ''}
    ${phase}${twiceDaily}
    ${tags ? `<span style="margin-left:4px;color:#555">${tags}</span>` : ''}
  </p>
  ${rows}
  <p class="footer">Generated ${new Date().toLocaleDateString()}</p>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  // Clean up blob URL after the window has loaded
  if (win) {
    win.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
  } else {
    // Fallback: direct download if popup blocked
    const link = document.createElement('a');
    link.href = url;
    link.download = `${plan.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.html`;
    link.click();
    URL.revokeObjectURL(url);
  }
};

// HTML Export
export const downloadProgramAsHTML = (program: Program): void => {
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <title>${program.name}</title>
    <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #333; }
        .day { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 5px; }
        .slot { margin: 10px 0; padding: 10px; background: #f5f5f5; border-radius: 3px; }
    </style>
</head>
<body>
    <h1>${program.name}</h1>
    ${program.description ? `<p>${program.description}</p>` : ''}
    ${program.days.map((day, dayIndex) => `
        <div class="day">
            <h2>${day.name}</h2>
            ${Object.entries(day.slots || {}).map(([slotId, slot]) => `
                <div class="slot">
                    <strong>Slot ${slotId}:</strong> ${slot.drillId || 'Empty'}
                    ${slot.sets ? `<br>Sets: ${slot.sets}` : ''}
                    ${slot.reps ? `<br>Reps: ${slot.reps}` : ''}
                    ${slot.rest ? `<br>Rest: ${slot.rest}` : ''}
                </div>
            `).join('')}
        </div>
    `).join('')}
</body>
</html>`;
    
    const dataBlob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${program.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.html`;
    link.click();
    URL.revokeObjectURL(url);
};

// CSV Export
export const downloadProgramAsCSV = (program: Program): void => {
    const rows: string[] = [];
    rows.push('Day,Slot,Drill ID,Sets,Reps,Rest,Tempo,Notes');
    
    program.days.forEach((day, dayIndex) => {
        Object.entries(day.slots || {}).forEach(([slotId, slot]) => {
            rows.push([
                day.name,
                slotId,
                slot.drillId || '',
                slot.sets || '',
                slot.reps || '',
                slot.rest || '',
                slot.tempo || '',
                (slot.notes || '').replace(/"/g, '""')
            ].map(cell => `"${cell}"`).join(','));
        });
    });
    
    const csvContent = rows.join('\n');
    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${program.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
};

// Import functions (for now, only JSON is fully implemented)
export const uploadProgramFromPDF = (): Promise<Program | null> => {
    return new Promise((resolve, reject) => {
        reject(new Error('PDF import not yet implemented. Please use JSON import.'));
    });
};

export const uploadProgramFromHTML = (): Promise<Program | null> => {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.html';
        
        input.onchange = (e: Event) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                resolve(null);
                return;
            }
            
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    // Basic HTML parsing - in production, use a proper parser
                    const html = event.target?.result as string;
                    // This is a simplified parser - would need more robust implementation
                    reject(new Error('HTML import parsing not yet fully implemented. Please use JSON import.'));
                } catch (error) {
                    logger.error('Error parsing HTML:', error);
                    reject(new Error('Invalid HTML file format'));
                }
            };
            
            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };
            
            reader.readAsText(file);
        };
        
        input.click();
    });
};

export const uploadProgramFromCSV = (): Promise<Program | null> => {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        
        input.onchange = (e: Event) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                resolve(null);
                return;
            }
            
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const csv = event.target?.result as string;
                    const lines = csv.split('\n');
                    if (lines.length < 2) {
                        reject(new Error('Invalid CSV format'));
                        return;
                    }
                    
                    // Parse CSV and convert to Program structure
                    // This is simplified - would need more robust CSV parsing
                    reject(new Error('CSV import parsing not yet fully implemented. Please use JSON import.'));
                } catch (error) {
                    logger.error('Error parsing CSV:', error);
                    reject(new Error('Invalid CSV file format'));
                }
            };
            
            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };
            
            reader.readAsText(file);
        };
        
        input.click();
    });
};

